// MAP HAZARDS — default telegraphs (owner 2026-11-02: hazards always telegraphed and fair). Every circle / rectangle zone
// gets a ground decal: WARN = a pulsing outline plus a fill that grows toward the edge as the moment approaches (a crush
// zone is red and fills completely right before it lands); ACTIVE = a soft tint in the hazard's colour. Global zones
// (storms) have no decal: their look is the fog / particles. Maps add set pieces on top (MapRenderDef.hazards).
// Render-only: reads sim.hazards.zones.
import * as THREE from 'three/webgpu';
import { attribute, color, float, length, max, min, mix, smoothstep, abs, uv, uniform, vec2, time, step } from 'three/tsl';
import type { Sim } from '../core/sim';
import { HazardField, HZ, type HazardKind } from '../core/hazards';
import type { GroundDeform } from './ground';
import { CB, type TelegraphKey } from './a11y';
import { JAMMER } from '../core/mapFoeDefs';

/** MAP FOES: patch kinds drawn in the matching hazard colours (spore cloud = slow, frozen ground = slip, lava = burn) */
const FOE_ZONE_KIND: HazardKind[] = ['slow', 'slip', 'burn'];
/** MAP FOES: Rime Wraith frozen ground: a deeper glacier blue than the pale slip colour, so it reads on snow */
const FOE_ICE = 0x3a8cff;
/** MAP FOES: the Hush Beacon aura ring (an effect area, not an attack: its own violet) */
const JAMMER_AURA = 0xb070ff;

export const HAZARD_COLOR: Record<HazardKind, number> = { burn: 0xff5a1e, slow: 0x5ac8ff, slip: 0xa8ecff, fog: 0xe0c890, crush: 0xff2438 };
const CAP = 160; // (map hazards + boss marks + MAP FOES marks / patches / auras)
/** comfort & accessibility: colour-blind palette roles (a11y.ts; Off = HAZARD_COLOR) and the warn pulse's rate / depth caps
 *  (Reduce flashing; view.ts sets them from FLASH each frame) */
const HAZARD_ROLE: Partial<Record<HazardKind, TelegraphKey>> = { burn: 'hazardBurn', slow: 'hazardSlow', slip: 'hazardSlip', crush: 'hazardCrush' };
export const hazardPulse = { rate: uniform(14), amp: uniform(0.25) };
export const hazardColor = (k: HazardKind): number => { const r = HAZARD_ROLE[k]; return r ? CB.tc(r) : HAZARD_COLOR[k]; };

export class HazardView {
  readonly mesh: THREE.InstancedMesh;
  private hz: THREE.InstancedBufferAttribute;
  private m = new THREE.Matrix4(); private q = new THREE.Quaternion(); private s = new THREE.Vector3(); private p = new THREE.Vector3(); private c = new THREE.Color();
  private up = new THREE.Vector3(0, 1, 0);

  constructor(scene: THREE.Scene, private ground: GroundDeform) {
    const geo = new THREE.PlaneGeometry(2, 2).rotateX(-Math.PI / 2);
    this.hz = new THREE.InstancedBufferAttribute(new Float32Array(CAP * 4), 4); this.hz.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('hz', this.hz);
    const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, fog: false, polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -4 });
    // hz = (warn progress 0..1, active 0/1, rectangle 0/1, crush 0/1)
    const H = attribute('hz', 'vec4');
    const q = uv().mul(2).sub(1);
    const d = mix(length(q), max(abs(q.x), abs(q.y)), H.z); // circle / square distance (rectangles are scaled squares)
    const edge = smoothstep(0.86, 0.95, d).mul(smoothstep(1.0, 0.965, d));
    const warn = H.x, act = H.y, crush = H.w;
    const pulse = time.mul(min(mix(float(5), float(14), warn.mul(crush)), hazardPulse.rate)).sin().mul(hazardPulse.amp).add(0.75);
    const fill = step(d, warn).mul(float(1).sub(act)).mul(mix(float(0.18), float(0.38), crush));
    const alpha = edge.mul(mix(pulse, float(0.55), act)).mul(0.95).add(fill).add(act.mul(float(1).sub(crush)).mul(0.2).mul(smoothstep(1.0, 0.5, d).mul(0.5).add(0.5)));
    mat.colorNode = color(0xffffff).mul(mix(float(1.25), float(1), act)); // x instanceColor (applied by three)
    mat.opacityNode = alpha.mul(step(d, float(1)));
    void vec2;
    this.mesh = new THREE.InstancedMesh(geo, mat, CAP);
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(CAP * 3), 3);
    this.mesh.frustumCulled = false; this.mesh.count = 0; this.mesh.renderOrder = 2;
    scene.add(this.mesh);
  }

  sync(sim: Sim): void {
    let n = 0;
    const H = this.hz.array as Float32Array;
    for (const z of sim.hazards.zones) {
      if (n >= CAP) break;
      if (z.global || (z.phase !== HZ.warn && z.phase !== HZ.active)) continue;
      const rect = !!z.l, sx = rect ? z.l! : z.r, sz = rect ? z.w ?? 1 : z.r;
      // sit on the highest ground under the footprint so hills do not swallow the decal
      const g = this.ground, cr = Math.cos(z.rot ?? 0), sr = Math.sin(z.rot ?? 0);
      let y = g.sample(z.x, z.z);
      for (const [a, b] of [[1, 0], [-1, 0], [0, 1], [0, -1], [0.7, 0.7], [-0.7, -0.7]]) y = Math.max(y, g.sample(z.x + (a * cr * sx - b * sr * sz) * 0.8, z.z + (a * sr * sx + b * cr * sz) * 0.8));
      this.p.set(z.x, y + 0.12, z.z); this.q.setFromAxisAngle(this.up, -(z.rot ?? 0)); this.s.set(sx, 1, sz);
      this.mesh.setMatrixAt(n, this.m.compose(this.p, this.q, this.s));
      this.mesh.setColorAt(n, this.c.setHex(hazardColor(z.kind)));
      H[n * 4] = HazardField.warnK(z); H[n * 4 + 1] = z.phase === HZ.active ? 1 : 0; H[n * 4 + 2] = rect ? 1 : 0; H[n * 4 + 3] = z.kind === 'crush' ? 1 : 0;
      n++;
    }
    // BOSS FORMS: a boss's impact marks ("rain") use the crush telegraph: outline + a fill growing to the moment it lands
    for (const m of sim.bossMarks) {
      if (n >= CAP) break;
      this.p.set(m.x, this.ground.sample(m.x, m.z) + 0.14, m.z); this.q.identity(); this.s.set(m.r, 1, m.r);
      this.mesh.setMatrixAt(n, this.m.compose(this.p, this.q, this.s));
      this.mesh.setColorAt(n, this.c.setHex(hazardColor('crush')));
      H[n * 4] = Math.min(1, m.t / m.warn); H[n * 4 + 1] = 0; H[n * 4 + 2] = 0; H[n * 4 + 3] = 1;
      n++;
    }
    // MAP FOES (owner 2026-11-02): attack marks (warn: outline + a fill growing to the strike; damaging = the crush colour,
    // a gust lane = the pale slip colour), ground patches (forming, then a soft active tint) and Hush Beacon auras
    const put = (x: number, z: number, rot: number, sx: number, sz: number, hex: number, warnK: number, act: number, rect: boolean, crush: boolean) => {
      // like the hazard decals: sit on the highest ground under the footprint (a long lane on a slope would sink into it)
      const cr = Math.cos(rot), sr = Math.sin(rot); let y = this.ground.sample(x, z);
      for (const [a, b] of [[1, 0], [-1, 0], [0.5, 0], [-0.5, 0], [0, 1], [0, -1]]) y = Math.max(y, this.ground.sample(x + (a * cr * sx - b * sr * sz) * 0.9, z + (a * sr * sx + b * cr * sz) * 0.9));
      this.p.set(x, y + 0.13, z); this.q.setFromAxisAngle(this.up, -rot); this.s.set(sx, 1, sz);
      this.mesh.setMatrixAt(n, this.m.compose(this.p, this.q, this.s)); this.mesh.setColorAt(n, this.c.setHex(hex));
      H[n * 4] = warnK; H[n * 4 + 1] = act; H[n * 4 + 2] = rect ? 1 : 0; H[n * 4 + 3] = crush ? 1 : 0; n++;
    };
    const F = sim.foes;
    if (F.active) {
      for (const m of F.marks) { if (n >= CAP) break; put(m.x, m.z, m.l > 0 ? m.rot : 0, m.l > 0 ? m.l : m.r, m.l > 0 ? m.w : m.r, hazardColor(m.hurt ? 'crush' : 'slip'), Math.min(1, m.t / m.warn), 0, m.l > 0, m.hurt); }
      for (const z of F.zones) { if (n >= CAP) break; const forming = z.t < z.form; put(z.x, z.z, 0, z.r, z.r, z.kind === 1 ? FOE_ICE : hazardColor(FOE_ZONE_KIND[z.kind]), forming ? z.t / z.form : 1, forming ? 0 : 1, false, false); }
      for (const e of sim.enemies) if (n < CAP && e.alive && e.def.kind === 'jammer' && e.edgeT < 0) put(e.x, e.z, 0, JAMMER.r, JAMMER.r, JAMMER_AURA, 1, 1, false, false);
    }
    this.mesh.count = n; this.mesh.visible = n > 0;
    if (n) { this.mesh.instanceMatrix.needsUpdate = true; this.mesh.instanceColor!.needsUpdate = true; this.hz.needsUpdate = true; }
  }
}
